import { useReducer, useState } from "react";
import { Outlet } from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import AddUserAutomaticallyModal from "./AddUserAutomaticallyModal";
import AddUserManuallyModal from "./AddUserManuallyModal";
import AddUserByShareQrCodeModal from "./AddUserByShareQrCodeModal";
import { useTranslation } from "react-i18next";
import AddToGroupModal from "./AddToGroupModal";
import UsersListComponent from "./UsersListComponent";
import { CustomButton } from "../../../atoms/CustomButton";
import { useGetEntityUsers } from "../../../../hooks/users";
import SecureStorage from "../../../../utils/secureStorage";
import AddUserTypeSelectionModal from "./AddUserTypeSelectionModal";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

enum AddUserActionKind {
  AU = "AU",
  MA = "MA",
  SH = "SH",
  DEFAULT = "DEFAULT",
  AG = "AG",
  TYPE_SELECTION = "TYPE_SELECTION",
}

interface AddUserAction {
  type: AddUserActionKind;
}

interface AddUserActionState {
  au: boolean;
  ma: boolean;
  sh: boolean;
  ag: boolean;
  typeSelection: boolean;
}

const addUserActionReducer = (
  state: AddUserActionState,
  action: AddUserAction
) => {
  const { type } = action;

  switch (type) {
    case AddUserActionKind.AU:
      return {
        au: true,
        ma: false,
        sh: false,
        ag: false,
        typeSelection: false,
      };
    case AddUserActionKind.MA:
      return {
        au: false,
        ma: true,
        sh: false,
        ag: false,
        typeSelection: false,
      };
    case AddUserActionKind.SH:
      return {
        au: false,
        ma: false,
        sh: true,
        ag: false,
        typeSelection: false,
      };
    case AddUserActionKind.AG:
      return {
        au: false,
        ma: false,
        sh: false,
        ag: true,
        typeSelection: false,
      };
    case AddUserActionKind.TYPE_SELECTION:
      return {
        au: false,
        ma: false,
        sh: false,
        ag: false,
        typeSelection: true,
      };
    default:
      return {
        au: false,
        ma: false,
        sh: false,
        ag: false,
        typeSelection: false,
      };
  }
};

const UsersList = () => {
  const [t] = useTranslation();
  const { user, customer_identification, userEndedSubscription } = useAuth();
  const permissions = usePermissions(user);

  const [limit, setLimit] = useState<12 | 30 | 50>(
    SecureStorage.getUsersListLimit() as 12 | 30 | 50
  );

  const [offset, setOffset] = useState(0);
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC" | "">("DESC");
  const [sortBy] = useState<"CREATION_DATE" | "EMAIL" | "">("");
  const [state, dispatch] = useReducer(addUserActionReducer, {
    au: false,
    ma: false,
    sh: false,
    ag: false,
    typeSelection: false,
  });

  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());

  const {
    isLoading,
    isError,
    isSuccess,
    data: res,
    refetch,
  } = useGetEntityUsers(
    customer_identification,
    user.entity_identification,
    limit,
    offset,
    sortBy,
    sortOrder,
    {
      onSuccess: (data) => {
        setLimit(data?.pagination?.limit ?? 12);
        setOffset(data?.pagination?.offset ?? 0);
      },
    }
  );

  const handleShowRelatedAddUserType = (
    addUserType: "au" | "ma" | "sh" | null | ""
  ) => {
    if (addUserType !== null && addUserType !== "") {
      switch (addUserType) {
        case "au":
          dispatch({ type: AddUserActionKind.AU });
          break;
        case "ma":
          dispatch({ type: AddUserActionKind.MA });
          break;
        case "sh":
          dispatch({ type: AddUserActionKind.SH });
          break;
        default:
          dispatch({ type: AddUserActionKind.DEFAULT });
      }
    }
  };

  const userTitle = (
    <div className="min-w-0">
      <h1 className="text-xl font-semibold text-gray-900">
        {t("dashboard.entities.viewEntityUser.key13")}
      </h1>
      {isSuccess && typeof res?.pagination?.total === "number" && (
        <p className="mt-0.5 text-sm text-gray-500">
          {t("dashboard.usersList.pageSubtitle", {
            count: res.pagination.total,
          })}
        </p>
      )}
    </div>
  );
  const addUserButton = permissions.can(Cap.IMPORT_USERS, Level.WRITE) ? (
    <CustomButton
      variant="primary"
      disabled={userEndedSubscription}
      icon="plus"
      title={t("dashboard.usersList.key1") as string}
      onClick={() => dispatch({ type: AddUserActionKind.TYPE_SELECTION })}
    />
  ) : null;

  const otherButtons = (
    <>
      {selectedRowIds?.size > 1 &&
        permissions.can(Cap.USERS_UPDATE, Level.WRITE) && (
          <CustomButton
            onClick={() => dispatch({ type: AddUserActionKind.AG })}
            title={t("dashboard.entities.viewEntityUser.key14")}
            icon="plus"
          />
        )}
    </>
  );
  return (
    <div className="container mx-auto py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        {userTitle}
        <div className="flex items-center gap-2">
          {otherButtons}
          {addUserButton}
        </div>
      </div>

      <div className="mb-3">
        <UsersListComponent
          users={res?.users}
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          refetch={refetch}
          selectedRowIds={selectedRowIds}
          setSelectedRowIds={setSelectedRowIds}
          pagination={res?.pagination}
          offset={offset}
          setOffset={setOffset}
          limit={limit}
          setLimit={setLimit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />

        <AddUserTypeSelectionModal
          show={state.typeSelection}
          onHide={() => dispatch({ type: AddUserActionKind.DEFAULT })}
          onSelectType={handleShowRelatedAddUserType}
        />

        <AddUserAutomaticallyModal
          show={state.au}
          onBackClick={() => {
            dispatch({ type: AddUserActionKind.DEFAULT });
          }}
          onHide={() => dispatch({ type: AddUserActionKind.DEFAULT })}
          size="lg"
          className="aum"
        />

        <AddUserManuallyModal
          show={state.ma}
          onBackClick={() => {
            dispatch({ type: AddUserActionKind.DEFAULT });
          }}
          onHide={() => dispatch({ type: AddUserActionKind.DEFAULT })}
          size="2xl"
          className="aum"
        />

        <AddUserByShareQrCodeModal
          show={state.sh}
          onBackClick={() => {
            dispatch({ type: AddUserActionKind.DEFAULT });
          }}
          onHide={() => dispatch({ type: AddUserActionKind.DEFAULT })}
          size="xl"
          className="aum"
        />
        <AddToGroupModal
          userList={selectedRowIds}
          show={state.ag}
          onHide={() => dispatch({ type: AddUserActionKind.DEFAULT })}
          onResolved={() => setSelectedRowIds(new Set())}
        />

        <Outlet />
      </div>
    </div>
  );
};

export default UsersList;
